<script>
    import { onMount } from 'svelte';
    import * as d3 from 'd3';
    import { coordinates, scroll } from './state';
    import { fade } from 'svelte/transition';

    export let metFacesData;
    export let clusterData;
    export let clusterRanges;
    export let generatedData;

    let viz;
    let height;
    let width;
    let scrollIndex = 0;
    let scrollProgress = 0;
    let scrollDirection = "down";

    let generatedTooltip;
    let generatedTooltipTop = 0;
    let generatedTooltipLeft = 0;
    let generatedTooltipDirection = "north";
    let generatedTooltipVisible = false;

    let tooltip;
    let tooltipTop = 0;
    let tooltipLeft = 0;
    let tooltipDirection = "north";
    let tooltipVisible = false;
    let selectedData = {};

    let hoverRep = [false, false, false, false, false, false, false, false, false, false]; //to reveal faces on hover
    let hoverCluster = 99; // to reveal cluster faces on hover
    $: selectedCluster = 99; //for updateAxis to zoom in to the right place
    let resetZoom = true; //zoom should not be applied when this is true

    window.onbeforeunload = function () {
            window.scrollTo(0,0);
    };

    const mobileBreakpoint = 900;
    $: imageSize = width < mobileBreakpoint ? 75 : 125;
    $: generatedImageSize = 140;
    $: blockSize = width < mobileBreakpoint ? 4 : 8;

    // let faceScale = 2.8;
    let miniSize = 20;

    const unsubscribeScroll = scroll.subscribe(({ index, progress, direction }) => {
        scrollIndex = index;
        scrollProgress = progress;
        scrollDirection = direction;
    });

    $: padding = { horizontal: width < mobileBreakpoint ? 45 : width / 8, vertical: width < mobileBreakpoint ? height / 5 : height / 10 };
    $: shorterAxisLength = Math.min(height - (padding.vertical*2), width - (padding.horizontal*2))

    $: scatterX = [1,2].includes(scrollIndex) ?
        d3.scaleLinear()
            .domain([0, 255])
            .range([padding.horizontal, shorterAxisLength+padding.horizontal])
        : ( ([1,2,3,4,5,6,7,8,9,10].includes(selectedCluster)) && (!resetZoom)) ?
        d3.scaleLinear()
            .domain([clusterRanges[selectedCluster-1].cluster_min_x, clusterRanges[selectedCluster-1].cluster_max_x])
            .range([padding.horizontal, shorterAxisLength+padding.horizontal])
        : 
        d3.scaleLinear()
            .domain([-1, 1])
            .range([padding.horizontal, shorterAxisLength+padding.horizontal]);

    $: scatterY = [1,2].includes(scrollIndex) ?
        d3.scaleLinear()
            .domain([0, 255])
            .range([height - padding.vertical, height - shorterAxisLength - padding.vertical])
        : ( ([1,2,3,4,5,6,7,8,9,10].includes(selectedCluster)) && (!resetZoom)) ?
        d3.scaleLinear()
            .domain([clusterRanges[selectedCluster-1].cluster_min_y, clusterRanges[selectedCluster-1].cluster_max_y])
            .range([height - padding.vertical, height - shorterAxisLength - padding.vertical])
        : 
        d3.scaleLinear()
            .domain([-1, 1])
            .range([height - padding.vertical, height - shorterAxisLength - padding.vertical])


    $: rows = Math.ceil( height / imageSize ) + 1;
    $: generatedRows = Math.ceil( height / generatedImageSize ) + 1;
    $: cols = Math.ceil( width / imageSize );

    onMount(() => {

        const svg = d3.select(viz);

        // d3.selectAll(".face-image").style("opacity", .8);

        svg.append("g")
            .attr("class", "face-markers")
            .selectAll("rect")
            .data(metFacesData, d => d.count)
            .join("rect")
                .attr("class", "face-marker")
                .attr("height", blockSize)
                .attr("width", blockSize)
                .style("fill", d => [].includes(scrollIndex) ? `url(#${d.count}-mini)` : d.dominant_color_hex)
                .style("stroke-width", 0)
                .style("stroke", "black")
                .on("mouseover", (e, d) => {
                    selectedData = {...d};
                    tooltipTop = `${e.clientY - 10}px`;
                    tooltipLeft = `${e.clientX}px`;

                    tooltipDirection = e.clientY < 200 ? "south" : "north";

                    tooltipVisible = true;
                })
                .on("mousemove", (e, d) => {
                    tooltipTop = `${e.clientY - 10}px`;
                    tooltipLeft = `${e.clientX}px`;
                })
                .on("mouseout", (e, d) => {
                    tooltipTop = `0px`;
                    tooltipLeft = `0px`;
                    tooltipVisible = false;
                });
                // .style("display", [0].includes(scrollIndex) ? "none" : "block")

        svg.append("g")
            .attr("class", "generated-face-markers")
            .selectAll("rect")
            .data(generatedData, d => d.filename)
            .join("rect")
                .attr("class", "generated-face-marker")
                .attr("height", generatedImageSize)
                .attr("width", generatedImageSize)
                .style("fill", d => `url(#${d.filename}-generated)`)
                .style("stroke-width", 0)
                .on("mouseover", (e, d) => {
                    selectedData = {...d};
                    generatedTooltipTop = `${e.clientY - 10}px`;
                    generatedTooltipLeft = `${e.clientX}px`;

                    generatedTooltipDirection = e.clientY < 200 ? "south" : "north";

                    generatedTooltipVisible = true;
                })
                .on("mousemove", (e, d) => {
                    generatedTooltipTop = `${e.clientY - 10}px`;
                    generatedTooltipLeft = `${e.clientX}px`;
                })
                .on("mouseout", (e, d) => {
                    generatedTooltipTop = `0px`;
                    generatedTooltipLeft = `0px`;
                    generatedTooltipVisible = false;
                })
                .style("opacity", scrollIndex === 5 ? 1 : 0)
                ;
                // .style("display", [5].includes(scrollIndex) ? "block" : "none")
                
        svg.append("g")
            .attr("class", "y-axis axis");
        
        svg.append("g")
            .attr("class", "x-axis axis");

        svg.append("text")
            .attr("class", "x-axis-label axis-label label")
            .style("text-anchor", "end")
            .style("fill", "#333333")
        
        svg.append("text")
            .attr("class", "y-axis-label axis-label label")
            .style("text-anchor", "start")
            .style("fill", "#333333")

    })

    $: d3.select(".y-axis")
        .attr("transform", `translate(${padding.horizontal}, 0)`)
        .style("display", [1,2].includes(scrollIndex) ? "block" : "none")
        .style("font", "15px Apercu")
    $: d3.select(".x-axis")
        .attr("transform", `translate(0, ${height - padding.vertical + 10})`)
        .style("display", [1,2].includes(scrollIndex) ? "block" : "none")
        .style("font", "15px Apercu")

    $: d3.select(".x-axis-label")
        .transition()
        .duration(((scrollIndex === 5 && scrollDirection === "down") || (scrollIndex === 4 && scrollDirection === "up")) ? 1000 : 0 )
        .attr("text-anchor", "end")
        .attr("x", (scatterX.range()[0] + scatterX.range()[1]) / 2)
        .attr("y", height - padding.vertical + 50)
        .style("display", [1,2].includes(scrollIndex) ? "block" : "none")
        .style("font", "19px Copyright Klim Type Foundry")
        .style("fill", (((scrollIndex === 1) || (scrollIndex === 2)) ? "var(--red)": "#333"))
        .text( [1,2].includes(scrollIndex) ? "Red level" : "Latent space vector 1 (points to be updated)")

    $: d3.select(".y-axis-label")
        .transition()
        .duration(((scrollIndex === 5 && scrollDirection === "down") || (scrollIndex === 4 && scrollDirection === "up")) ? 1000 : 0 )
        .attr("text-anchor", "end")
        .attr("x", padding.horizontal + 10)
        .attr("y", scatterY.range()[1] + (width < mobileBreakpoint ? 10 : 10))
        .style("display", [1,2].includes(scrollIndex) ? "block" : "none")
        .style("font", "19px Copyright Klim Type Foundry")
        .style("fill", scrollIndex === 1 ? "var(--green)" : scrollIndex === 2 ? "steelblue": "#333")
        .text(scrollIndex === 1 ? "Green level" : "Blue level"); //scrollIndex === 2 ? "Blue level" : "Latent space vector 2 (points to be updated)");

    $: xAxisScale = scatterX;
    $: yAxisScale = scatterY;

    $: xAxis = d3.axisBottom()
        .scale(xAxisScale)
        .tickFormat(d3.format("d"))
        .ticks(4);
    $: yAxis = d3.axisLeft()
        .scale(yAxisScale)
        .tickFormat(d3.format(","))
        .ticks(4)

    $: d3.selectAll(".grid").style("display", scrollIndex === 6 ? "block" : "none")
       
    $: if (scrollIndex === 0) {
        const svg = d3.select(viz);

        svg.selectAll(".face-image")
            .transition()
            .duration(0)
            .attr("width", imageSize)
            .attr("height", imageSize)

        svg.selectAll(".face-marker")
            .style("cursor", "default")
            .transition()
            .duration(scrollDirection === "up" ? 1000 : 0)
            .attr("width", imageSize)
            .attr("height", imageSize)
            .attr("x", (d, i) => {
                return imageSize * Math.floor(i / rows);
            })
            .attr("y", (d, i) => {
                return imageSize * ( i % rows);
            })
            .attr("rx", 0)
            .attr("ry", 0)
            .style("opacity", .8)
            .transition()
            .duration(0)
            .style("fill", d => `url(#${d.count})`)  
    }

    $: if (scrollIndex === 1 || scrollIndex === 2) {
        
        const svg = d3.select(viz);
        const blockTransitionTime = 1000;

        svg.selectAll(".face-marker")
            .style("cursor", "crosshair")
            .style("display", d => d)
            .attr("rx", 30)
            .attr("ry", 30)
            .transition()
            .duration(blockTransitionTime)
            .style("fill", d => d.dominant_color_hex)
            .style("opacity", .8)
            .attr("x", d => scatterX(d.r))
            .attr("y", d => scatterY(scrollIndex === 1 ? d.g : d.b))
            .attr("width", blockSize)
            .attr("height", blockSize)

        svg.select(".x-axis")
            .transition()
            .duration(((scrollIndex === 2 && scrollDirection === "down") || (scrollIndex === 1 && scrollDirection === "up")) ? blockTransitionTime : 0 )
            .call(xAxis);

        svg.select(".y-axis")
            .transition()
            .duration((scrollIndex === 5 && scrollDirection === "up") ? 0 : blockTransitionTime)
            .call(yAxis);
    }

    $: if (scrollIndex === 3) {

        const svg = d3.select(viz);
        const blockTransitionTime = 1000;

        svg.selectAll(".face-marker")
            .style("cursor", "crosshair")
            .style("display", d => d)
            .attr("rx", 30)
            .attr("ry", 30)
            .transition()
            .duration(blockTransitionTime)
            .style("fill", d => d.dominant_color_hex)
            .style("opacity", .8)
            .attr("x", d => scatterX(d.umap_jitter_X))
            .attr("y", d => scatterY(d.umap_jitter_Y))
            .attr("width", blockSize)
            .attr("height", blockSize);

    }

    $: if (scrollIndex === 4) {
        
        const svg = d3.select(viz);
        const blockTransitionTime = 1500;

        svg.selectAll(".face-marker")
            // .style("fill", d => `url(#${d.count}-tiny)`)
            .style("cursor", "crosshair")
            .style("display", d => d)
            .attr("rx", 0)
            .attr("ry", 0)
            .attr("width", d => resetZoom ? miniSize : 40)
            .attr("height", d => resetZoom ? miniSize : 40)
            .style("fill", d => ( ( (hoverCluster == 99) && (resetZoom) || ((d.cluster == `Cluster ${hoverCluster}`) && (resetZoom)) ) ? `url(#${d.count}-mini)` : d.cluster == `Cluster ${selectedCluster}` ? `url(#${d.count}-mini-40)` : d.dominant_color_hex))
            .transition()
            .duration(blockTransitionTime)
            .style("opacity", 1)
            .attr("x", d => scatterX(d.gridX))
            .attr("y", d => scatterY(d.gridY))
            .attr("width", d => resetZoom ? miniSize : 40)
            .attr("height", d => resetZoom ? miniSize : 40)
            .transition()
            .duration(blockTransitionTime)
            .style("opacity", d => ((selectedCluster == 99) || (d.cluster == `Cluster ${selectedCluster}`) || (hoverCluster != selectedCluster)) ? 1 : 0)
            .filter(d => d.cluster == `Cluster ${selectedCluster}`)
            .attr("x", (d, i) => {
                if (hoverCluster != selectedCluster) {
                    return scatterX(d.gridX);
                }
                return imageSize * Math.floor(i / rows);
            })
            .attr("y", (d, i) => {
                if (hoverCluster != selectedCluster) {
                    return scatterY(d.gridY);
                }
                return imageSize * ( i % rows);
            })
            .style("fill", d => (hoverCluster == selectedCluster) ? `url(#${d.count})` : `url(#${d.count}-mini-40)`)
            .attr("width", d => d.cluster == `Cluster ${selectedCluster}` ? imageSize : 0)
            .attr("height", d => d.cluster == `Cluster ${selectedCluster}` ? imageSize : 0)
            ;

        svg.selectAll(".generated-face-marker")
            .transition()
            .duration(500)
            .style("opacity", 0)
            .attr("x", -125)
            .attr("y", -125);
    }

    $: legendDisplay = scrollIndex === 4 ? "display: grid" : "display: none;";
    $: gridDisplay = scrollIndex === 6;
    $: vidDisplay = scrollIndex > 6;

    function updateAxis() {

        const svg = d3.select(viz);

        // svg.selectAll(".face-marker")
        //     .style("cursor", "crosshair")
        //     .style("display", d => d)
        //     // .filter(d => d.cluster == `Cluster ${selectedCluster}`)
        //     .attr("rx", 0)
        //     .attr("ry", 0)
        //     .attr("width", hoverCluster == selectedCluster ? 40 : blockSize)
        //     .attr("height", hoverCluster == selectedCluster ? 40 : blockSize)
        //     .style("opacity", 1)
        //     .style("fill", d => d.cluster == `Cluster ${selectedCluster}` ? `url(#${d.count}-mini-40)` : d.dominant_color_hex)
        //     ;

        resetZoom = false;
    };

    $: if (scrollIndex != 4) hoverCluster = 99;

    $: if (scrollIndex === 5) {

        const svg = d3.select(viz);
        const blockTransitionTime = 1000;

        svg.selectAll(".generated-face-image")
            .transition()
            .duration(0)
            .attr("width", generatedImageSize)
            .attr("height", generatedImageSize)

        svg.selectAll(".face-marker")
            .style("cursor", "crosshair")
            .style("display", d => d)
            .transition()
            .duration(blockTransitionTime)
            .attr("rx", 30)
            .attr("ry", 30)
            .style("opacity", 0)
            .style("fill", d => d.dominant_color_hex)
            .attr("width", 2)
            .attr("height", 2);
        
        svg.selectAll(".generated-face-marker")
            .style("cursor", "crosshair")
            .style("display", d => d)
            .attr("rx", 0)
            .attr("ry", 0)
            .transition()
            .duration(blockTransitionTime)
            .style("opacity", 1)
            .attr("x", (d, i) => {
                return generatedImageSize * Math.floor(i / generatedRows);
            })
            .attr("y", (d, i) => {
                return generatedImageSize * ( i % generatedRows);
            })
            .attr("width", generatedImageSize)
            .attr("height", generatedImageSize);


    }

    $: if (scrollIndex === 6) {

        const svg = d3.select(viz);

        svg.selectAll(".generated-face-marker")
            .transition()
            .duration(1000)
            .style("opacity", 0)
            .transition()
            .duration(1000)
            .attr("x", 0)
            .attr("y", 0);

    }

    $: if (scrollIndex === 7) {

        

        }

    // These values are bound to properties of the video
	let time = 0;
	let duration;
	let paused = true;

	let showControls = true;
	let showControlsTimeout;

	function handleMousemove(e) {
		// Make the controls visible, but fade out after
		// 2.5 seconds of inactivity
		clearTimeout(showControlsTimeout);
		showControlsTimeout = setTimeout(() => showControls = false, 2500);
		showControls = true;

		if (!(e.buttons & 1)) return; // mouse not down
		if (!duration) return; // video not loaded yet

		const { left, right } = this.getBoundingClientRect();
		time = duration * (e.clientX - left) / (right - left);
	}

	function handleMousedown(e) {
		// we can't rely on the built-in click event, because it fires
		// after a drag — we have to listen for clicks ourselves

		function handleMouseup() {
			if (paused) e.target.play();
			else e.target.pause();
			cancel();
		}

		function cancel() {
			e.target.removeEventListener('mouseup', handleMouseup);
		}

		e.target.addEventListener('mouseup', handleMouseup);

		setTimeout(cancel, 200);
	}

	function format(seconds) {
		if (isNaN(seconds)) return '...';

		const minutes = Math.floor(seconds / 60);
		seconds = Math.floor(seconds % 60);
		if (seconds < 10) seconds = '0' + seconds;

		return `${minutes}:${seconds}`;
	}

</script>

<style>
    .figure {
        width: 100vw;
        height: 100vh;
        margin: 0;
    }

    .viz-tile {
        height: 100vh;
        width: 100vw;
    }

    .generatedTooltip {
        position: absolute;
        text-align: center;
        top: 0;
        background-color: rgba(255, 255, 255, 0.9);
        padding: 1rem;
        font-size: 0.85rem;
        z-index: 101;
        border-radius: 2px;
        box-shadow: 0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22);
    }

    .generatedTooltip-header {
        margin-bottom: 0.5rem;
        text-align: center;
        font-size: 1.1em;
        font-family: "Copyright Klim Type Foundry", serif;
    }

    .generatedTooltip-text {
        text-align: center;
        font-size: 1em;
        margin-top: .15rem;
        font-family: "Apercu", sans-serif;
    }

    .tooltip {
        position: absolute;
        text-align: center;
        top: 0;
        background-color: rgba(255, 255, 255, 0.9);
        padding: 1rem;
        font-size: 0.85rem;
        z-index: 101;
        border-radius: 2px;
        box-shadow: 0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22);
    }

    .tooltip-header {
        margin-bottom: 0.5rem;
        text-align: center;
        font-size: 1.1em;
        font-family: "Copyright Klim Type Foundry", serif;
    }

    .tooltip-text {
        text-align: center;
        font-size: 1em;
        margin-top: .15rem;
        font-family: "Apercu", sans-serif;
    }

    :global(.label) {
        font-size: 0.9rem;
    }
    @media only screen and (max-width: 900px) {
        :global(.label) {
            font-size: 0.8rem;
        }
    }

    .genGrid {
        position: absolute;
        top: 0;
        left: 0;
        height: 100vh;
    }

    .legend {
        position: absolute;
        top: 8%;
        left: 62.1vw;
        z-index: 100;

        background-color: rgba(255, 255, 255, 0.95);
        padding: 0.4rem 1rem 0.7rem 1rem;
        border-radius: 5px;
/*         
        height: 15rem;
        width: 15rem; */

        display: grid;
        grid-template-columns: 100px 100px 100px 100px 100px; 
        /* 2fr 3fr 3fr 2fr; */
        align-items: center;
        
        row-gap: 1vh;
        column-gap: 1vh;
    }

    @media only screen and (max-width: 900px) {
        .legend {
            top: 5%;
            left: 50%;
            transform: translateX(-50%);
        }
    }

    .legend__title {
        grid-column: 1 / 6;
        margin: 0 auto;
        font-size: 1rem;
        font-family: "Apercu", sans-serif;
    }

    .pointer {
        cursor: zoom-in;
    }

    .controls {
		position: absolute;
		top: 0;
		width: 100%;
		transition: opacity 1s;
        font-size: 1rem;
        font-family: "Apercu", sans-serif;

	}

	.info {
		display: flex;
		width: 100%;
		justify-content: space-between;
	}

	span {
		padding: 0.2em 0.5em;
		color: white;
		text-shadow: 0 0 8px black;
		font-size: 1.4em;
		opacity: 0.7;
	}

	.time {
		width: 3em;
	}

	.time:last-child { text-align: right }

	progress {
		display: block;
		width: 100%;
		height: 10px;
		-webkit-appearance: none;
		appearance: none;
	}

	progress::-webkit-progress-bar {
		background-color: rgba(0,0,0,0.2);
	}

	progress::-webkit-progress-value {
		background-color: rgba(255,255,255,0.6);
	}

	video {
		width: 50vw;
	}

    .genVid {
        position: absolute;
        top: 25vh;
        left: 9vw;
        /* width: 100vw; */
    }

</style>

<figure class="figure" bind:clientWidth={width} bind:clientHeight={height} id="fig">
    <svg bind:this={viz} class="viz-tile">
        <defs>
            {#each metFacesData as {image_path, count}, i}
                <pattern patternUnits={"objectBoundingBox"} height={1} width={1} key={i} id={count}>
                    <image href={`images/compressed_${image_path}`} id={`${count}-photo`} class={"face-image"} x={0} y={0} />
                </pattern>
                <pattern patternUnits={"objectBoundingBox"} height={1} width={1} key={i} id={`${count}-mini`}>
                        <image href={`images/mini_${image_path}`} id={`${count}-photo-mini`} class={"face-image-mini"} x={0} y={0} height=20 width=20/>
                </pattern>
                <pattern patternUnits={"objectBoundingBox"} height={1} width={1} key={i} id={`${count}-mini-40`}>
                    <image href={`images/mini_${image_path}`} id={`${count}-photo-mini-40`} class={"face-image-mini-40"} x={0} y={0} height=40 width=40/>
                </pattern>
            {/each}
            {#each generatedData as {filename}, i}
                <pattern patternUnits={"objectBoundingBox"} height={1} width={1} key={i} id={`${filename}-generated`}>
                    <image href={`images/generated_images/${filename}`} id={`${i}-generated-photo`} class={"generated-face-image"} x={0} y={0} />
                </pattern>
            {/each}
        </defs>
    </svg>
    <div bind:this={tooltip} class="tooltip" style="display: {tooltipVisible && width > mobileBreakpoint && scrollIndex > 0 ? "block" : "none"}; top: {tooltipTop}; left: {tooltipLeft}; transform: translate(-50%, {tooltipDirection === "north" ? "-100%" : "15%"});">
        <div class="tooltip-header"><strong>{selectedData.title || "Unknown"}</strong></div>
        <img src={`images/compressed_${selectedData.image_path}`} alt={`${selectedData.count}-photo`} width={imageSize} />
        <div class="tooltip-text">
             by {selectedData.artist_display_name || "Unknown"}
        </div>
    </div>

    <div bind:this={generatedTooltip} class="generatedTooltip" style="display: {generatedTooltipVisible && width > mobileBreakpoint && scrollIndex > 0 ? "block" : "none"}; top: {generatedTooltipTop}; left: {generatedTooltipLeft}; transform: translate(-50%, {generatedTooltipDirection === "north" ? "-100%" : "15%"});">
        <div class="generatedTooltip-header"><strong>{selectedData.rnn_caption || "Unknown"}</strong></div>
        <img src={`images/generated_images/${selectedData.filename}`} alt={`${selectedData.caption}`} width={generatedImageSize} />
        <div class="generatedTooltip-text">
             by Met A.I. (2021)
        </div>
    </div>
</figure>

<div class="legend" style={legendDisplay} on:mouseleave|self|stopPropagation="{e => {resetZoom = true; hoverCluster = 99; selectedCluster = 99;}}">
    <div class="legend__title">Cluster representatives</div>
    {#each clusterData as {cluster_rep, label}, i}
        <div on:mouseenter="{e => {hoverCluster = i+1; hoverRep[i] = true;} }"
             on:mouseout|self|stopPropagation="{e => hoverRep[i] = false}"
             on:click="{e => {selectedCluster = i+1; updateAxis();} }"
             id={i} class="pointer">
            <img src={`images/compressed_images/${cluster_rep}`} id={`cluster-${i}-rep`} class={"cluster-rep"} alt={`${label} Representative`} width={.8*imageSize} height={.8*imageSize} style={`opacity:${hoverRep[i] ? 1 : .7}; background-color:white`}/>
        </div>
    {/each}
</div>

{#if gridDisplay}
    <div transition:fade class="genGrid">
        <img src="images/genGrid.png" alt="grid of generated faces interpolated" height=100% />
    </div>
{/if}

{#if vidDisplay}
    <div transition:fade class="genVid">
        <video 
            src="images/metFaceReal.mp4"
            on:mousemove={handleMousemove}
            on:mousedown={handleMousedown}
            bind:currentTime={time}
            bind:duration
            bind:paused>
            <track kind="captions">
        </video>
        
        <div class="controls" style="opacity: {duration && showControls ? 1 : 0}">
            <progress value="{(time / duration) || 0}"/>

            <div class="info">
                <span class="time">{format(time)}</span>
                <span>click anywhere to {paused ? 'play' : 'pause'} / drag to seek</span>
                <span class="time">{format(duration)}</span>
            </div>
        </div>
    </div>
{/if}